/**
 * Platform service
 *
 * handle loading flow of platform services
 * - init all needed services
 * - please check this graph to understand the flow : https://evident-products.atlassian.net/wiki/spaces/WSCLOUD/pages/111772838/ADR+-+Authentification+--+Frontend+implementation#Application-Auth-Flow
 */
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivationStart, ResolveEnd, Router } from '@angular/router';
import {
  filter,
  first,
  firstValueFrom,
  forkJoin,
  from,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';

import type { LoadingState, Maybe } from '@evc/web-components';
import {
  LOADING_STATES,
  ToastService,
  VERSION as WEB_COMPONENTS_VERSION,
} from '@evc/web-components';

import { ENVIRONMENT } from '../lib/features/config/env.type';
import { AuthService } from './clients/core/auth/service/auth.service';
import { OrganizationsService } from './clients/core/organizations/organizations.service';
import { Organization } from './clients/core/organizations/organizations.type';
import { UserService } from './clients/core/user/user.service';
import { UserProfile } from './clients/core/user/user.type';
import { PlatformConfigService } from './features/config/config.service';
import { I18nPlatformService } from './features/i18n/i18n.service';
import type { AbstractI18nService } from './features/i18n/i18n-service.type';
import { LanguageService } from './features/i18n/language.service';
import type { LangChangeEvent } from './features/i18n/language.type';
import { activatedRoute$ } from './features/utilities/router.utils';
import { VERSION as PLATFORM_VERSION } from './version';

export type PlatformInitStatus = {
  ready: boolean;
  useConnect: boolean;
  connected: boolean;
  toid?: string;
  lang: string;
  translations?: Record<string, unknown>;
};

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  #authService = inject(AuthService);
  #userService = inject(UserService);
  #organizationsService = inject(OrganizationsService);
  #languageService = inject(LanguageService);
  #i18nPlatformService = inject(I18nPlatformService);
  #platformConfigService = inject(PlatformConfigService);
  #toastService = inject(ToastService);
  #router = inject(Router, { optional: true });

  /** because outside of platform, we handle it if provided */
  #i18nAppService: Maybe<AbstractI18nService>;

  #state = signal<LoadingState>(LOADING_STATES.PRISTINE);

  /** Infrastructure ready: auth, i18n, user/org loaded. Used by LoaderComponent. */
  initialized = computed((): Maybe<true> => {
    if (this.#state() !== LOADING_STATES.READY) return undefined;
    if (!this.#authService.isAuthenticated()) return true;

    return this.#userService.ready() || undefined;
  });

  /** App-level ready: initialized, organization confirmed, and license capacity available */
  ready = computed(() => {
    if (!this.initialized()) return undefined;
    if (this.#authService.shouldPromptOrganizationSelection()) return undefined;
    if (this.#authService.shouldPromptLicenseCapacity()) return undefined;
    if (this.#authService.isRedirectingToCreateOrganization()) return undefined;

    return true as const;
  });
  ready$ = toObservable(this.ready).pipe(
    filter((v): v is true => v === true),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // please use init() - it will return this promise
  #initializationPromise: Maybe<Promise<PlatformInitStatus>>;

  #userLang$ = toObservable(this.#userService.profile).pipe(
    filter((profile) => !!profile),
    map((profile) => profile!.preferredLanguage),
  );

  constructor() {
    // eslint-disable-next-line no-console
    console.debug('👋 from Evident Connect', {
      platform: PLATFORM_VERSION,
      webComponents: WEB_COMPONENTS_VERSION,
    });
  }

  /** init platform services
   * (A loader may be displayed during this process)
   * - (maybe) wait for auth complete
   * - wait for language detection
   * - wait for i18n translations
   * - wait for first route to be resolved
   * then initialize core services (user / organizations)
   *
   * Will be automatically called by <evc-platform> component
   * But ok to call this yourself prior to use <evc-platform>
   */
  async init(i18nAppService?: Maybe<AbstractI18nService>): Promise<PlatformInitStatus> {
    const debug = this.#platformConfigService.getEnv('DEBUG_PLATFORM');
    const env = this.#platformConfigService.getEnv('ENV');
    if (debug && env && env !== ENVIRONMENT.production && env !== ENVIRONMENT.staging) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).platformService = this;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).platformConfigService = this.#platformConfigService;
    }

    if (i18nAppService) this.#i18nAppService = i18nAppService;

    // if already initialized - just return the promise previously created
    if (!this.#initializationPromise) {
      this.#scrollTopOnNavigation();
      this.#registerFloatingLicenseReleaseBeforeLogout();

      this.#initializationPromise = this.#initializationFlow();
    }

    return this.#initializationPromise;
  }

  #initializationFlow(): Promise<PlatformInitStatus> {
    const useConnect: boolean = this.#platformConfigService.useConnect();

    const waitRoute = this.#waitFirstRoute(useConnect);
    let waitUserAndOrganizations: Maybe<Promise<[Maybe<UserProfile>, Organization[]]>>;
    this.#logFlowStep('start')();

    return this.#waitMaybeConnected(useConnect)
      .then(this.#logFlowStep('... user authenticated:'))
      .then((connected: boolean) => {
        const flow = Promise.all([
          this.#getAuthOrganizationId(connected).then(
            this.#logFlowStep('... auth in organization id:'),
          ),
          this.#waitLanguage(connected).then(this.#logFlowStep('... lang detected:')),
        ]);

        // trigger load now - wait promise completion later
        if (useConnect)
          waitUserAndOrganizations = firstValueFrom(this.#waitUserAndOrganizations$());

        return flow.then(
          ([toid, lang]) =>
            ({ useConnect, connected, toid, lang }) as Omit<
              PlatformInitStatus,
              'ready' | 'i18n' | 'route'
            >,
        );
      })
      .then((status: Omit<PlatformInitStatus, 'ready' | 'i18n' | 'route'>) =>
        this.#waitI18n()
          .then(this.#logFlowStep('... i18n ready'))
          .then((i18n) => ({ ...status, ...i18n }) as Omit<PlatformInitStatus, 'ready' | 'route'>),
      )
      .then((status: Omit<PlatformInitStatus, 'ready' | 'route'>) =>
        waitRoute
          .then(this.#logFlowStep('... route ready'))
          .then((route) => ({ ...status, route }) as Omit<PlatformInitStatus, 'ready'>),
      )
      .then((status: Omit<PlatformInitStatus, 'ready'>) => {
        if (!useConnect || !waitUserAndOrganizations) return status;

        return firstValueFrom(this.#authService.isRouteProtected$()).then((isProtectedRoute) => {
          this.#logFlowStep('... is route protected :')(isProtectedRoute);

          if (!isProtectedRoute) return status;
          this.#logFlowStep('... wait user and organization')();

          return waitUserAndOrganizations!
            .then(this.#logFlowStep('... user and organization ready :'))
            .then(() => ({
              ...status,
              // may have been updated by user preferred lang
              lang: this.#languageService.lang() || status.lang,
            }));
        });
      })
      .then((status) => {
        this.#state.set(LOADING_STATES.READY);

        return status;
      })
      .catch((error) => {
        this.#state.set(LOADING_STATES.ERROR);

        console.error('PlatformService init error', error);
      })
      .then((status) => ({ ...status, ready: this.ready() }) as PlatformInitStatus)
      .then(this.#logFlowStep('✓ DONE'))
      .then((status) => {
        this.#handleNotificationUrlParam();

        return status;
      });
  }

  #handleNotificationUrlParam(): void {
    // Check for ?notification param and show toast if present
    const url = new URL(window.location.href);
    const urlParams = url.searchParams;
    const notification = urlParams.get('notification');
    if (!notification) return;

    const message = this.#i18nPlatformService.t(`notifications.${notification}`);
    if (notification) {
      this.#toastService.info(message);
    }

    // Silently remove the notification param from the URL
    urlParams.delete('notification');
    window.history.replaceState(
      {},
      '',
      url.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '') + url.hash,
    );
  }

  /** wait connection (fetch accessToken) if needed
   * - skip if legacy mode
   * - succeed after user token - but auth complete flow still in progress
   *   => may trigger init userService
   *   @see waitAuthComplete 👇
   * @returns connected:boolean (if true will have to wait auth complete)
   */
  async #waitMaybeConnected(withAuth: boolean): Promise<boolean> {
    if (!withAuth) {
      this.#logFlowStep('skip auth')('legacy');

      return Promise.resolve(false);
    }

    return this.#waitMaybeLogoutRoute().then((isLogout: boolean) => {
      if (isLogout) {
        this.#logFlowStep('skip auth')('logout');

        return false;
      }

      this.#logFlowStep('wait for (maybe) auth')();

      return this.#authService.init();
    });
  }

  /** after connected, wait for auth complete to return organizationId from token
   * (some application may not enforce user to have organization)
   * @returns toid:string (current organizationId) or false
   */
  async #getAuthOrganizationId(authSession: boolean): Promise<string | boolean> {
    if (!authSession) return Promise.resolve(false);

    return firstValueFrom(this.#authService.onComplete$).then((isAuthenticated) => {
      if (!isAuthenticated) return false;

      return this.#authService.organizationId() ?? false;
    });
  }

  /** We finally guesse language to use
   * (may wait user or try many sources)
   * - init i18nService to load translation files
   * @returns lang:string (current lang to use)
   */
  async #waitLanguage(withAuth: boolean): Promise<string> {
    // sync lang change for later
    this.#languageService.lang$.subscribe((lang: string) => {
      this.#i18nAppService?.setLanguage(lang);

      const platformSafeLang = this.#languageService.platformSafeLang();
      this.#authService.language = platformSafeLang;
      this.#i18nPlatformService.setLanguage(platformSafeLang);
    });

    return this.#languageService.init(withAuth);
  }

  /** after language, fetch translation messages
   * - now transtations ready to use
   * @returns {lang:string, translations:[]} | false
   */
  async #waitI18n(): Promise<LangChangeEvent | false> {
    const flow = this.#i18nAppService?.onReady$ ?? of<false>(false);

    return Promise.all([
      firstValueFrom<LangChangeEvent | false>(flow),
      firstValueFrom(this.#i18nPlatformService.onReady$),
    ]).then(([event]) => event);
  }

  /** wait for the first route to be resolved
   * @returns Promise<void>
   */
  async #waitFirstRoute(useConnect: boolean): Promise<boolean> {
    if (!this.#router || !useConnect) {
      return Promise.resolve(true);
    }

    const resolvedRoute$ = merge(
      activatedRoute$(this.#router),
      this.#router.events.pipe(filter((event) => event instanceof ResolveEnd)),
    ).pipe(
      first(),
      map(() => true),
    );

    return firstValueFrom(resolvedRoute$);
  }

  /** wait route data to check if logout (would cancel init auth flow)
   */
  async #waitMaybeLogoutRoute(): Promise<boolean> {
    if (!this.#router) {
      return Promise.resolve(false);
    }

    const isLogoutRoute$ = merge(
      activatedRoute$(this.#router),
      this.#router.events.pipe(
        filter((event) => event instanceof ActivationStart),
        map((route) => route?.snapshot),
      ),
    ).pipe(
      first(),
      map((routeConfig) => routeConfig.data?.logout ?? false),
    );

    return firstValueFrom(isLogoutRoute$);
  }

  /**
   * attempt init user and organizations service when user is authenticated
   * and may use user preferred language
   * > will not block if error (offline, legacy...)
   */
  #waitUserAndOrganizations$(): Observable<[Maybe<UserProfile>, Organization[]]> {
    // user preferred language may override current locale
    this.#userLang$.subscribe((lang) => {
      this.#languageService.setUserLanguage(lang);
    });

    return this.#authService.accessToken$.pipe(
      filter((token) => !!token),
      mergeMap(() =>
        forkJoin<[Maybe<UserProfile>, Organization[]]>([
          from(this.#initUserService()),
          from(this.#initOrganizationService()),
        ]),
      ),
      tap(() => {
        this.#clearUserAndOrganizationOnLogout();
      }),
    );
  }

  #initUserService(): Promise<Maybe<UserProfile>> {
    return this.#userService
      .init()
      .then((profile) => {
        this.#authService.loginHint = profile?.email;

        return profile;
      })
      .catch((_error) => undefined);
  }

  #initOrganizationService(): Promise<Organization[]> {
    const toid = this.#authService.organizationId();

    return this.#organizationsService.init(toid).catch((_error) => []);
  }

  #registerFloatingLicenseReleaseBeforeLogout(): void {
    if (!this.#platformConfigService.useConnect()) return;

    this.#authService.beforeLogoutHook.set(() => this.#userService.releaseFloatingLicense());
  }

  #clearUserAndOrganizationOnLogout(): void {
    this.#authService.accessToken$
      .pipe(
        filter((token) => !token),
        tap(() => this.#organizationsService.clear()),
        tap(() => this.#userService.clear()),
      )
      .subscribe();
  }

  confirmOrganization(orgId: string): void {
    const currentOrgId = this.#authService.organizationId();

    if (orgId === currentOrgId) {
      this.#authService.dismissOrganizationSelectionPrompt();
    } else {
      // Update local org state without triggering re-login, then redirect
      // with promptOrgSelection:false so the prompt doesn't reappear after the switch
      this.#organizationsService.setCurrent(orgId);
      this.#authService
        .login({
          extraQueryParameters: { toid: orgId },
          state: JSON.stringify({ promptOrgSelection: false }),
        })
        .subscribe();
    }
  }

  #scrollTopOnNavigation(): void {
    this.#router?.events
      .pipe(
        filter((event) => event instanceof ResolveEnd),
        tap(() => {
          setTimeout(() => {
            window.scrollTo(0, 0);
            document.body.scrollTo(0, 0);
          }, 0);
        }),
      )
      .subscribe();
  }

  #logFlowStep(name: string) {
    const debug = this.#platformConfigService.getEnv('DEBUG_PLATFORM') ?? false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (value?: any) => {
      // eslint-disable-next-line no-console
      if (debug) console.debug(`[platform] initialization : ${name}`, value ?? '');

      return value;
    };
  }
}
